/**
 * Centralized color palette - Single source of truth for all colors in the application.
 *
 * All colors are derived from the OKLCH tokens defined in apps/web/src/app/globals.css.
 * This module provides:
 * - Pre-computed hex equivalents for contexts that can't use CSS variables (Mapbox, SVG, Chakra theme)
 * - Full 50-900 Chakra color scales at each palette hue
 * - Semantic token mappings
 * - Chart color arrays
 *
 * OKLCH Token Reference (from globals.css):
 *   --primary:     oklch(0.6436 0.1104 223.6487)
 *   --destructive: oklch(0.6356 0.2082 25.3782)
 *   --chart-1:     oklch(0.6436 0.1104 223.6487) (same as primary)
 *   --chart-2:     oklch(0.5599 0.1529 257.5105)
 *   --chart-3:     oklch(0.6393 0.0970 194.9999)
 *   --chart-4:     oklch(0.7421 0.0750 230.7652)
 *   --chart-5:     oklch(0.7947 0.0458 248.4024)
 */

// ---------------------------------------------------------------------------
// Semantic Tokens (hex equivalents of CSS custom properties)
// ---------------------------------------------------------------------------

export const tokens = {
  // Core
  primary: '#2b8fba',           // --primary / --chart-1
  destructive: '#d44a3a',       // --destructive (light mode)
  destructiveDark: '#6b2520',   // --destructive (dark mode, oklch 0.3901 0.1297)

  // Chart colors
  chart1: '#2b8fba',            // --chart-1 (blue)
  chart2: '#5b63c7',            // --chart-2 (purple)
  chart3: '#3d9e8f',            // --chart-3 (teal)
  chart4: '#7ab5d1',            // --chart-4 (light blue)
  chart5: '#a3b8d4',            // --chart-5 (pale blue)

  // Light mode backgrounds/foregrounds
  background: '#f8fafc',        // --background (light)
  foreground: '#1e3a4a',        // --foreground (light)
  card: '#ffffff',              // --card (light)
  muted: '#f3f6f8',            // --muted (light)
  mutedForeground: '#6b8090',   // --muted-foreground (light)
  secondary: '#edf2f5',         // --secondary (light)
  accent: '#ebf1f4',            // --accent (light)
  border: '#d1dae4',            // --border (light)
  input: '#dce4eb',             // --input (light)

  // Dark mode backgrounds/foregrounds
  backgroundDark: '#1a2332',    // --background (dark)
  foregroundDark: '#fafcfd',    // --foreground (dark)
  cardDark: '#1f2e3d',          // --card (dark)
  mutedDark: '#283541',         // --muted (dark)
  mutedForegroundDark: '#9ca3af', // --muted-foreground (dark)
  secondaryDark: '#2a3d4d',     // --secondary (dark)
  accentDark: '#2f4252',        // --accent (dark)
  borderDark: '#374151',        // --border (dark)

  // Sidebar
  sidebar: '#f5f8fa',           // --sidebar (light)
  sidebarForeground: '#2a4a5e', // --sidebar-foreground (light)
  sidebarDark: '#1c2a38',       // --sidebar (dark)
  sidebarForegroundDark: '#dce4eb', // --sidebar-foreground (dark)
} as const;

// ---------------------------------------------------------------------------
// Chart Colors Array (for chart components)
// ---------------------------------------------------------------------------

export const CHART_COLORS = [
  tokens.chart1,  // #2b8fba - blue
  tokens.chart2,  // #5b63c7 - purple
  tokens.chart3,  // #3d9e8f - teal
  tokens.chart4,  // #7ab5d1 - light blue
  tokens.chart5,  // #a3b8d4 - pale blue
] as const;

// ---------------------------------------------------------------------------
// Chakra UI Color Scales (50-900)
// Each scale is derived by varying lightness at the token's OKLCH hue
// ---------------------------------------------------------------------------

export const scales = {
  /**
   * Blue scale - derived from --primary (oklch hue 223.6)
   * Used for: primary actions, info status, colorScheme="blue"
   */
  blue: {
    50: '#f0f8fc',
    100: '#d6eef8',
    200: '#abe0f2',
    300: '#7bcde8',
    400: '#4fb6d8',
    500: '#2b8fba',   // --primary
    600: '#23769a',
    700: '#1b5d79',
    800: '#134459',
    900: '#0b2b39',
  },

  /**
   * Gray scale - derived from neutral tokens (oklch hue ~218)
   * Used for: text, borders, backgrounds, colorScheme="gray"
   */
  gray: {
    50: '#f8fafb',
    100: '#f1f5f7',
    200: '#e2e8ed',
    300: '#cbd5dd',
    400: '#94a3b3',
    500: '#6b8090',
    600: '#4d6070',
    700: '#374858',
    800: '#243240',
    900: '#131d28',
  },

  /**
   * Red scale - derived from --destructive (oklch hue 25.4)
   * Used for: errors, offline status, critical alerts, colorScheme="red"
   */
  red: {
    50: '#fef2f1',
    100: '#fcdad7',
    200: '#f9b5af',
    300: '#f28a80',
    400: '#e86355',
    500: '#d44a3a',   // --destructive
    600: '#b33c2f',
    700: '#8c2f25',
    800: '#66221b',
    900: '#401511',
  },

  /**
   * Green/Teal scale - derived from --chart-3 (oklch hue 195)
   * Used for: success, online status, healthy, colorScheme="green"
   */
  green: {
    50: '#eefaf8',
    100: '#d2f2ed',
    200: '#a5e6db',
    300: '#70d4c5',
    400: '#4fbfae',
    500: '#3d9e8f',   // --chart-3
    600: '#328275',
    700: '#27665d',
    800: '#1c4a44',
    900: '#112e2b',
  },

  /**
   * Orange scale - derived from destructive hue shifted warmer (hue ~35)
   * Used for: warnings, colorScheme="orange"
   */
  orange: {
    50: '#fef5f0',
    100: '#fce4d5',
    200: '#f9c9ab',
    300: '#f3a87a',
    400: '#eb864e',
    500: '#d4672a',   // warm variant of destructive hue
    600: '#b35523',
    700: '#8c431b',
    800: '#663114',
    900: '#40200c',
  },

  /**
   * Purple scale - derived from --chart-2 (oklch hue 257.5)
   * Used for: maintenance, multiple violations, colorScheme="purple"
   */
  purple: {
    50: '#f3f3fc',
    100: '#e2e3f7',
    200: '#c5c8ef',
    300: '#a3a8e4',
    400: '#8287d6',
    500: '#5b63c7',   // --chart-2
    600: '#4a51a6',
    700: '#3a4085',
    800: '#2a2f64',
    900: '#1b1f43',
  },

  /**
   * Teal scale - alias for green (same palette token, chart-3)
   * Used for: colorScheme="teal"
   */
  teal: {
    50: '#eefaf8',
    100: '#d2f2ed',
    200: '#a5e6db',
    300: '#70d4c5',
    400: '#4fbfae',
    500: '#3d9e8f',   // --chart-3
    600: '#328275',
    700: '#27665d',
    800: '#1c4a44',
    900: '#112e2b',
  },

  /**
   * Cyan scale - derived from --chart-4 (oklch hue 230.8)
   * Used for: info highlights, colorScheme="cyan"
   */
  cyan: {
    50: '#f0f7fb',
    100: '#d9ecf6',
    200: '#b3daee',
    300: '#8dc7e4',
    400: '#7ab5d1',   // --chart-4
    500: '#5a9ab8',
    600: '#487d99',
    700: '#366179',
    800: '#254559',
    900: '#142a39',
  },

  /**
   * Yellow scale - warm neutral (hue ~45)
   * Used for: low severity, colorScheme="yellow"
   */
  yellow: {
    50: '#fef9f0',
    100: '#fcefd5',
    200: '#f9dfab',
    300: '#f3cb7a',
    400: '#ebb64e',
    500: '#d49b2a',
    600: '#b38123',
    700: '#8c661b',
    800: '#664b14',
    900: '#40300c',
  },

  /**
   * Pink scale - derived from destructive shifted cooler (hue ~350)
   * Used for: colorScheme="pink"
   */
  pink: {
    50: '#fef1f5',
    100: '#fcd9e5',
    200: '#f9b3cb',
    300: '#f28aaf',
    400: '#e86390',
    500: '#d44370',
    600: '#b3365d',
    700: '#8c2a4a',
    800: '#661e37',
    900: '#401324',
  },
} as const;

// ---------------------------------------------------------------------------
// Semantic Status Colors (for programmatic use)
// ---------------------------------------------------------------------------

export const statusColors = {
  success: scales.green[500],     // #3d9e8f (chart-3)
  warning: scales.orange[500],    // #d4672a
  error: scales.red[500],         // #d44a3a (destructive)
  info: scales.blue[500],         // #2b8fba (primary)
  neutral: scales.gray[400],      // #94a3b3
} as const;

// ---------------------------------------------------------------------------
// Badge Color Classes - Theme-aware Tailwind utilities for badges/tags
// Uses proper contrast ratios for both light and dark modes
// ---------------------------------------------------------------------------

export const badgeColors = {
  // Status badges
  error: 'bg-red-50 dark:bg-red-950 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800',
  warning: 'bg-orange-50 dark:bg-orange-950 text-orange-700 dark:text-orange-300 border border-orange-200 dark:border-orange-800',
  success: 'bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800',
  info: 'bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800',
  neutral: 'bg-secondary text-secondary-foreground border border-border',

  // Semantic badges
  red: 'bg-red-50 dark:bg-red-950 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800',
  orange: 'bg-orange-50 dark:bg-orange-950 text-orange-700 dark:text-orange-300 border border-orange-200 dark:border-orange-800',
  yellow: 'bg-yellow-50 dark:bg-yellow-950 text-yellow-700 dark:text-yellow-300 border border-yellow-200 dark:border-yellow-800',
  green: 'bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800',
  teal: 'bg-teal-50 dark:bg-teal-950 text-teal-700 dark:text-teal-300 border border-teal-200 dark:border-teal-800',
  blue: 'bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800',
  purple: 'bg-purple-50 dark:bg-purple-950 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-800',
  pink: 'bg-pink-50 dark:bg-pink-950 text-pink-700 dark:text-pink-300 border border-pink-200 dark:border-pink-800',
  gray: 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700',

  // Special cases
  black: 'bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 border border-gray-800 dark:border-gray-200',
  white: 'bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 border border-gray-200 dark:border-gray-700',
  cyan: 'bg-cyan-50 dark:bg-cyan-950 text-cyan-700 dark:text-cyan-300 border border-cyan-200 dark:border-cyan-800',
} as const;

// ---------------------------------------------------------------------------
// Violation Type Badge Colors - Mapped by violation type code
// ---------------------------------------------------------------------------

export const violationBadgeColors: Record<string, string> = {
  SEATBELT: badgeColors.red,
  NO_SEATBELT: badgeColors.red,
  MOBILE: badgeColors.orange,
  PHONE_USE: badgeColors.orange,
  MULTIPLE_VIOLATIONS: badgeColors.purple,
  NO_VIOLATION: badgeColors.success,
  NO_HELMET: badgeColors.blue,
  SPEEDING: badgeColors.red,
  SPEED: badgeColors.red,
  WRONG_DIRECTION: badgeColors.pink,
  TRIPLE_RIDING: badgeColors.blue,
  RED_LIGHT: badgeColors.red,
  BUS_LANE: badgeColors.orange,
  DEFAULT: badgeColors.neutral,
};

// ---------------------------------------------------------------------------
// Vehicle Color Badge Classes - Maps vehicle colors to badge styles
// ---------------------------------------------------------------------------

export const vehicleColorBadges: Record<string, string> = {
  RED: badgeColors.red,
  BLUE: badgeColors.blue,
  GREEN: badgeColors.green,
  YELLOW: badgeColors.yellow,
  ORANGE: badgeColors.orange,
  PURPLE: badgeColors.purple,
  PINK: badgeColors.pink,
  WHITE: badgeColors.white,
  BLACK: badgeColors.black,
  GRAY: badgeColors.gray,
  GREY: badgeColors.gray,
  SILVER: badgeColors.gray,
  BROWN: badgeColors.orange,
  GOLD: badgeColors.yellow,
  CYAN: badgeColors.cyan,
  TEAL: badgeColors.teal,
  DEFAULT: badgeColors.neutral,
};

// ---------------------------------------------------------------------------
// Status Badge Colors - Maps status strings to badge styles
// ---------------------------------------------------------------------------

export const statusBadgeColors: Record<string, string> = {
  // General statuses
  COMPLETED: badgeColors.success,
  SUCCESS: badgeColors.success,
  APPROVED: badgeColors.success,
  ACTIVE: badgeColors.success,
  ONLINE: badgeColors.success,
  HEALTHY: badgeColors.success,

  PROCESSING: badgeColors.blue,
  IN_PROGRESS: badgeColors.blue,
  RUNNING: badgeColors.blue,

  PENDING: badgeColors.yellow,
  WAITING: badgeColors.yellow,
  SCHEDULED: badgeColors.yellow,

  FAILED: badgeColors.error,
  ERROR: badgeColors.error,
  REJECTED: badgeColors.error,
  OFFLINE: badgeColors.error,
  UNHEALTHY: badgeColors.error,

  WARNING: badgeColors.warning,
  DEGRADED: badgeColors.warning,

  CANCELLED: badgeColors.neutral,
  UNKNOWN: badgeColors.neutral,
  DEFAULT: badgeColors.neutral,
};

// ---------------------------------------------------------------------------
// Helper Functions
// ---------------------------------------------------------------------------

/**
 * Get badge classes for a violation type
 */
export function getViolationBadgeClasses(violationType: string): string {
  return violationBadgeColors[violationType?.toUpperCase()] || violationBadgeColors.DEFAULT;
}

/**
 * Get badge classes for a vehicle color
 */
export function getVehicleColorBadgeClasses(color: string): string {
  return vehicleColorBadges[color?.toUpperCase()] || vehicleColorBadges.DEFAULT;
}

/**
 * Get badge classes for a status
 */
export function getStatusBadgeClasses(status: string): string {
  return statusBadgeColors[status?.toUpperCase()] || statusBadgeColors.DEFAULT;
}
